import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as ArrowDownSvg } from 'src/assets/svg/arrow-down.svg'
import { ReactComponent as CheckedAllSvg } from 'src/assets/svg/checked.svg'
import { ReactComponent as CheckAllSvg } from 'src/assets/svg/checkbox-white.svg'
import { ReactComponent as DeselectSvg } from 'src/assets/svg/deselect.svg'
import { ReactComponent as GlobeSvg } from 'src/assets/svg/Globe.svg'
import { ReactComponent as SentimentActionIcon } from 'src/assets/svg/sentiment-analysis-action.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { Skeleton } from 'src/components/Skeleton'
import {
  CommentSentiment,
  useUpdateContentAnalysisCommentSentimentMutation,
} from 'src/reactQuery/useUpdateContentAnalysisCommentSentimentMutation'
import { ChartComment } from 'src/pages/ContentAnalysisDetail/mockData/commentsChartMockData'
import styles from './commentsList.module.scss'
import { CommentItem } from '../CommentItem'
// The loading rows reuse the real CommentItem stylesheet so their footprint is
// taken from the component being replaced rather than re-guessed here.
import itemStyles from '../CommentItem/commentItem.module.scss'

type SentimentTab = 'all' | 'positive' | 'neutral' | 'negative'
type Platform =
  | 'all'
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'tiktok'
  | 'twitter'
  | 'youtube'

// Real dimensions from commentItem.module.scss: a .commentItem is 16px padding
// top/bottom around a 16px-tall .header (+8px margin) and a single clamped 14px
// /1.6 line -> ~78px. .list in commentsList.module.scss is min/max-height 350px,
// so the list box itself cannot shift; these only stop the "No comments found"
// empty state from being asserted while the comments are still loading.
const COMMENT_SKELETON_COUNT = 4
const COMMENT_HEADER_HEIGHT = 16
const COMMENT_AUTHOR_WIDTH = 116
const COMMENT_DATE_WIDTH = 74
const COMMENT_TEXT_HEIGHT = 22

interface CommentsListProps {
  analysisId: string
  comments: ChartComment[]
  dateRangeKey?: string
  isLoading?: boolean
  platforms?: string[]
}

const TABS: { label: string; value: SentimentTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Positive', value: 'positive' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Negative', value: 'negative' },
]

// The menu behind the sentiment icon. Picking one re-labels every selected
// comment via PATCH /content-analysis/:id/comments/sentiment, which also keeps
// the analysis doc's rollup and the per-post breakdowns in step server-side.
const SENTIMENT_ACTIONS: { key: CommentSentiment; label: string }[] = [
  { key: 'positive', label: 'Positive' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'negative', label: 'Negative' },
]

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const buildPlatformOptions = (
  platforms?: string[]
): { label: string; value: Platform }[] => {
  const all: { label: string; value: Platform } = {
    label: 'All Platforms',
    value: 'all',
  }
  if (!platforms || platforms.length === 0) return [all]
  return [
    all,
    ...platforms.map((p) => ({
      label: platformDisplayLabel(p, capitalize(p)),
      value: p as Platform,
    })),
  ]
}

const getPlatformIcon = (platform: Platform): ReactNode => {
  if (platform === 'all') {
    return <GlobeSvg />
  }
  return SOCIAL_ICONS[platform]
}

export const CommentsList: FC<CommentsListProps> = ({
  analysisId,
  comments,
  dateRangeKey,
  isLoading = false,
  platforms: platformsProp,
}) => {
  const platformOptions = useMemo(
    () => buildPlatformOptions(platformsProp),
    [platformsProp]
  )
  const [activeTab, setActiveTab] = useState<SentimentTab>('all')
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isSentimentMenuOpen, setIsSentimentMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const sentimentMenuRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    onOutsideClick: () => setIsDropdownOpen(false),
    ref: dropdownRef,
  })

  useOutsideClick({
    onOutsideClick: () => setIsSentimentMenuOpen(false),
    ref: sentimentMenuRef,
  })

  // The top-bar date range lives outside this component and this component
  // never remounts when it changes — without this, a selection made in one
  // range survived into another, and "Mark as" re-labeled rows the user could
  // no longer see. Same rule as the tab/platform filters: rows the user has
  // lost sight of are rows they have lost track of.
  useEffect(() => {
    setSelectedIds(new Set())
    setIsSentimentMenuOpen(false)
  }, [dateRangeKey])

  const { isPending: isSentimentUpdating, mutate: updateCommentSentiment } =
    useUpdateContentAnalysisCommentSentimentMutation({
      onSuccess: () => {
        setSelectedIds(new Set())
        setIsSentimentMenuOpen(false)
      },
    })

  const filteredComments = useMemo(() => {
    const result = comments.filter((comment) => {
      const matchesSentiment =
        activeTab === 'all' || comment.sentiment === activeTab
      const matchesPlatform =
        selectedPlatform === 'all' || comment.platform === selectedPlatform
      return matchesSentiment && matchesPlatform
    })

    return [...result].reverse()
  }, [comments, activeTab, selectedPlatform])

  const selectedPlatformData = platformOptions.find(
    (p) => p.value === selectedPlatform
  )

  const toggleSelect = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)

      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }

      return next
    })
  }

  const isAllVisibleSelected =
    filteredComments.length > 0 &&
    filteredComments.every((comment) => selectedIds.has(comment.id))

  const selectAllVisible = () => {
    setSelectedIds(new Set(filteredComments.map((comment) => comment.id)))
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
    setIsSentimentMenuOpen(false)
  }

  // Switching a filter hides rows, and a selection the user can no longer see
  // is a selection they have lost track of — so both filters start over.
  const handlePlatformSelect = (platform: Platform) => {
    setSelectedPlatform(platform)
    setIsDropdownOpen(false)
    clearSelection()
  }

  const handleTabSelect = (tab: SentimentTab) => {
    setActiveTab(tab)
    clearSelection()
  }

  // The menu stays open while the request runs: success clears the selection
  // and closes it, while an error leaves it up so the user can retry.
  const handleSentimentSelect = (sentiment: CommentSentiment) => {
    if (selectedIds.size === 0 || isSentimentUpdating) return
    updateCommentSentiment({
      commentIds: Array.from(selectedIds),
      id: analysisId,
      sentiment,
    })
  }

  return (
    <div className={styles.commentsList}>
      <div className={styles.header}>
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              className={classNames(styles.tab, {
                [styles.active]: activeTab === tab.value,
              })}
              key={tab.value}
              onClick={() => handleTabSelect(tab.value)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.platformDropdownWrapper} ref={dropdownRef}>
          <button
            className={classNames(styles.platformSelect, {
              [styles.open]: isDropdownOpen,
            })}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            type="button"
          >
            <span className={styles.platformIcon}>
              {getPlatformIcon(selectedPlatform)}
            </span>
            <span>{selectedPlatformData?.label}</span>
            <ArrowDownSvg
              className={classNames(styles.dropdownArrow, {
                [styles.open]: isDropdownOpen,
              })}
            />
          </button>
          {isDropdownOpen && (
            <div className={styles.platformDropdown}>
              {platformOptions.map((platform) => (
                <button
                  className={classNames(styles.platformOption, {
                    [styles.selected]: selectedPlatform === platform.value,
                  })}
                  key={platform.value}
                  onClick={() => handlePlatformSelect(platform.value)}
                  type="button"
                >
                  <span className={styles.platformIcon}>
                    {getPlatformIcon(platform.value)}
                  </span>
                  <span>{platform.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedIds.size > 0 && (
        <div className={styles.selectionBar}>
          {/* Same pair as Social Listening's toolbar. Select All covers the
              rows the user can see (current tab + platform). */}
          <button
            aria-label="Select all comments"
            className={styles.selectionIcon}
            onClick={selectAllVisible}
            type="button"
          >
            {isAllVisibleSelected ? (
              <CheckedAllSvg aria-hidden="true" />
            ) : (
              <CheckAllSvg aria-hidden="true" />
            )}
            <span className={styles.selectionHint}>Select All</span>
          </button>

          <button
            aria-label="Deselect all comments"
            className={styles.selectionIcon}
            onClick={clearSelection}
            type="button"
          >
            <DeselectSvg aria-hidden="true" />
            <span className={styles.selectionHint}>Deselect all</span>
          </button>

          <span className={styles.selectionDivider} />

          <div className={styles.selectionActionWrap} ref={sentimentMenuRef}>
            <button
              aria-label="Change selected comment sentiment"
              className={classNames(styles.selectionAction, {
                [styles.active]: isSentimentMenuOpen,
              })}
              onClick={() => setIsSentimentMenuOpen((open) => !open)}
              type="button"
            >
              <SentimentActionIcon aria-hidden="true" />
            </button>

            {isSentimentMenuOpen && (
              <div className={styles.sentimentMenu}>
                {SENTIMENT_ACTIONS.map((action, index) => (
                  <button
                    className={styles.sentimentOption}
                    disabled={isSentimentUpdating}
                    key={action.key}
                    onClick={() => handleSentimentSelect(action.key)}
                    type="button"
                  >
                    <span
                      className={classNames(
                        styles.sentimentDot,
                        styles[action.key]
                      )}
                    />
                    <span>Mark as:</span>
                    <strong>{action.label}</strong>
                    {index < SENTIMENT_ACTIONS.length - 1 && (
                      <span className={styles.sentimentDivider} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className={styles.selectionText}>
            Selected comment: <strong>({selectedIds.size})</strong>
          </span>
        </div>
      )}

      <div className={styles.list}>
        {/* Loading is checked BEFORE the empty state: an empty `comments` array
            during the first load is "not here yet", not "none found". */}
        {isLoading ? (
          Array.from({ length: COMMENT_SKELETON_COUNT }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={itemStyles.commentItem} key={index}>
              <div className={itemStyles.header}>
                <div className={itemStyles.platformInfo}>
                  <Skeleton circle width={COMMENT_HEADER_HEIGHT} />
                  <Skeleton
                    height={COMMENT_HEADER_HEIGHT}
                    width={COMMENT_AUTHOR_WIDTH}
                  />
                </div>
                <Skeleton
                  height={COMMENT_HEADER_HEIGHT}
                  width={COMMENT_DATE_WIDTH}
                />
              </div>
              <Skeleton height={COMMENT_TEXT_HEIGHT} width="100%" />
            </div>
          ))
        ) : filteredComments.length === 0 ? (
          <div className={styles.emptyState}>No comments found</div>
        ) : (
          filteredComments.map((comment) => (
            <CommentItem
              comment={comment}
              isSelected={selectedIds.has(comment.id)}
              key={comment.id}
              onToggleSelect={toggleSelect}
            />
          ))
        )}
      </div>
    </div>
  )
}
